import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createCurriculum } from '../../hooks/useCurriculum';
import useQueryParams from '../../hooks/useQueryParams';
import windowService from '../../services/windowService';
import { useUser } from '../authentication';
import { BrandCode } from '../curriculum';
import { ErrorMessage } from '../status';
import { LicenseAlreadyGranted, PreviewAlreadyGranted } from './components/AlreadyGranted';
import { OnboardingContent } from './components/OnboardingContent';
import { BodyStyle, ContentContainer, Header } from './components/OnboardingElements';
import { onboardingConfig, OnboardingType, OnboardingTypeConfig } from './models/onboarding';
import { getPreviewCurriculum, getTrialCurriculum } from './utils/getCurriculum';
import { grantPreviewLicenses, grantTrialLicensesFromTrialCode } from './utils/grantLicenses';

const onboardingTypeConfig = {
  trial: {
    getCurriculum: getTrialCurriculum,
    grantLicenses: grantTrialLicensesFromTrialCode,
    alreadyGrantedComponent: (redirectUrl?: string) => <LicenseAlreadyGranted subscriptionUrl={redirectUrl} />,
  },
  preview: {
    getCurriculum: getPreviewCurriculum,
    grantLicenses: grantPreviewLicenses,
    alreadyGrantedComponent: () => <PreviewAlreadyGranted />,
  },
} satisfies Record<OnboardingType, OnboardingTypeConfig>;

const convertParamToArray = (param?: string) => {
  if (!param) return [];
  return param.split(',').map(item => item.trim());
};

export const Onboarding = () => {
  const { user } = useUser();
  const { brandCode } = useParams<{ brandCode: BrandCode }>();
  const brand = createCurriculum(brandCode)?.brand;
  const preselectedAges = convertParamToArray(useQueryParams()?.ages);
  const config = brand ? onboardingConfig[brand.code as BrandCode] : null;
  const typeConfig = config ? onboardingTypeConfig[config.type] : null;
  const isDeactivated = !!config?.deactivated;

  useEffect(() => {
    if (isDeactivated) {
      windowService.redirectTo('/bible-studies');
    }
  }, [isDeactivated]);

  if (!brand?.code || !config || !typeConfig) {
    return <ErrorMessage />;
  }

  return (
    <div className="pb-64">
      <BodyStyle background={config.background || ''} />
      <Header>{brand.logo || <img src={brand.imgSrc} alt={brand.name} />}</Header>
      <ContentContainer>
        <OnboardingContent
          userId={user?.userId}
          userEmail={user?.logInEmail}
          brandCode={brand.code}
          type={config.type}
          getCurriculum={typeConfig.getCurriculum}
          grantLicenses={typeConfig.grantLicenses}
          alreadyGrantedComponent={typeConfig.alreadyGrantedComponent(config.subscriptionUrl)}
          preselectedAges={preselectedAges}
        />
      </ContentContainer>
    </div>
  );
};
